// Manifest validator — the boundary between persisted JSON and the
// renderer. Use this when reading a manifest from the DB / API / KV /
// URL hash; it returns `null` for any input that doesn't conform to the
// current schema, so a corrupted row never reaches the renderer.

import { ALL_GENERATIVE_VARIANTS } from '../core/dot-engine/types';
import { OUTCOME_HUES } from '../presets/outcome-palettes';
import { PALETTE_NAMES } from '../presets/palettes';
import type { GenerativeVariant } from '../types';
import type { ManifestSource, PaletteRef, PatternManifest } from './types';
import { MANIFEST_VERSION } from './types';

const GENERATIVE_VARIANTS: readonly GenerativeVariant[] = ALL_GENERATIVE_VARIANTS;

// ALLOW-UNKNOWN: runtime type-guard for arbitrary parsed JSON input
function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

// ALLOW-UNKNOWN: runtime type-guard for arbitrary parsed JSON input
function parsePaletteRef(input: unknown): PaletteRef | null {
  if (!isObject(input)) return null;
  if (input.kind === 'preset') {
    const name = input.name;
    if (typeof name === 'string' && (PALETTE_NAMES as readonly string[]).includes(name)) {
      return { kind: 'preset', name: name as (typeof PALETTE_NAMES)[number] };
    }
    return null;
  }
  if (input.kind === 'outcome-hue') {
    const hue = input.hue;
    if (typeof hue === 'string' && (OUTCOME_HUES as readonly string[]).includes(hue)) {
      return { kind: 'outcome-hue', hue: hue as (typeof OUTCOME_HUES)[number] };
    }
    return null;
  }
  if (input.kind === 'category') {
    const category = input.category;
    // Empty string is accepted: the downstream resolver
    // (`resolvePaletteRef` / `resolveCategoryPlaceholderSource`) falls back
    // to the default palette + placeholder for unknown categories, so a
    // missing category is harmless rather than invalid.
    if (typeof category === 'string') {
      return { kind: 'category', category };
    }
    return null;
  }
  return null;
}

// ALLOW-UNKNOWN: runtime type-guard for arbitrary parsed JSON input
function parseSource(input: unknown): ManifestSource | null {
  if (!isObject(input)) return null;
  if (input.kind === 'url') {
    const url = input.url;
    if (typeof url === 'string' && url.length > 0) {
      return { kind: 'url', url };
    }
    return null;
  }
  if (input.kind === 'synthetic') {
    const variant = input.variant;
    const seed = input.seed;
    if (
      typeof variant === 'string' &&
      (GENERATIVE_VARIANTS as readonly string[]).includes(variant) &&
      typeof seed === 'number' &&
      Number.isFinite(seed)
    ) {
      return { kind: 'synthetic', variant: variant as GenerativeVariant, seed };
    }
    return null;
  }
  return null;
}

/**
 * Migrate a v1 manifest payload to v2 in place. V1 had `sourceUrl: string`
 * directly; v2 wraps it into `source: { kind: 'url', url }`. Returns the
 * normalised v2 input or `null` if the v1 shape is invalid.
 */
function migrateV1ToV2(input: Record<string, unknown>): Record<string, unknown> | null {
  if (typeof input.sourceUrl !== 'string' || input.sourceUrl === '') return null;
  return {
    version: MANIFEST_VERSION,
    source: { kind: 'url', url: input.sourceUrl },
    palette: input.palette,
    seed: input.seed,
  };
}

/**
 * Validate an unknown value against the current `PatternManifest` schema.
 * Returns the validated manifest, or `null` for any input that fails
 * shape / version / enum checks. Migrates v1 → v2 transparently.
 *
 * Use this at the consumer boundary (DB read, API response, URL param)
 * — anywhere the manifest isn't lib-controlled.
 */
// ALLOW-UNKNOWN: public boundary validator — input arrives untyped from DB / API / URL hash
export function parsePatternManifest(input: unknown): PatternManifest | null {
  if (!isObject(input)) return null;

  let normalised: Record<string, unknown> = input;

  // V1 → V2 migration: legacy payloads had a flat `sourceUrl` field.
  if (input.version === 1) {
    const migrated = migrateV1ToV2(input);
    if (migrated === null) return null;
    normalised = migrated;
  } else if (input.version !== MANIFEST_VERSION) {
    return null;
  }

  const source = parseSource(normalised.source);
  if (source === null) return null;
  if (typeof normalised.seed !== 'number' || !Number.isFinite(normalised.seed)) return null;
  const palette = parsePaletteRef(normalised.palette);
  if (palette === null) return null;
  return {
    version: MANIFEST_VERSION,
    source,
    palette,
    seed: normalised.seed,
  };
}
